import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import UserManagement from './UserManagement';
import ChangePassword from './ChangePassword';
import GmailIntegration from './GmailIntegration';
import styles from './Setting.module.scss';
import EstimateSettings from './estimate-settings/EstimateSettings';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

const Settings = () => {
  const { t } = useTranslation(['admin/settings']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  // Get tab value from URL params, default to 0
  const getTabFromUrl = (params: URLSearchParams) => {
    const tabParam = params.get('tab');
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
        return tabIndex;
      }
    }
    return 0;
  };

  const [tabValue, setTabValue] = useState(() => getTabFromUrl(searchParams));

  // Sync tab value with URL params on mount and when URL changes
  useEffect(() => {
    const urlTab = getTabFromUrl(searchParams);
    setTabValue(urlTab);
  }, [searchParams]);

  // Open change password modal if tab 1 is selected
  useEffect(() => {
    if (tabValue === 1) {
      setIsChangePasswordOpen(true);
    }
  }, [tabValue]);

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
    // Update URL with new tab value
    const newSearchParams = new URLSearchParams(searchParams);
    if (newValue === 0) {
      // Remove tab param for default tab
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', newValue.toString());
    }
    setSearchParams(newSearchParams, { replace: true });

    if (newValue === 1) {
      setIsChangePasswordOpen(true);
    }
  };

  const handleCloseChangePassword = () => {
    setIsChangePasswordOpen(false);
    // Navigate back to first tab
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('tab');
    setSearchParams(newSearchParams, { replace: true });
    setTabValue(0);
  };

  const tabs = [
    { index: 0, label: t('userManagement.title') },
    { index: 1, label: t('changePassword.title') },
    { index: 2, label: t('estimateSettings.title') },
    { index: 3, label: t('gmailIntegration.title') },
  ];

  return (
    <div className={cx('settings-container')}>
      <div className={cx('settings-content')}>
        <div className={cx('tabs-container')}>
          <div
            className={cx('tab-list')}
            role="tablist"
            aria-label="settings tabs"
          >
            {tabs.map((tab) => (
              <button
                key={tab.index}
                type="button"
                role="tab"
                id={`settings-tab-${tab.index}`}
                aria-selected={tabValue === tab.index}
                aria-controls="settings-tabpanel"
                className={cx('tab-item', { 'tab-item-selected': tabValue === tab.index })}
                onClick={() => handleTabChange(tab.index)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div
          role="tabpanel"
          id="settings-tabpanel"
          aria-labelledby={`settings-tab-${tabValue}`}
          className={cx('tab-panel')}
        >
          {tabValue === 0 && <UserManagement />}
          {tabValue === 1 && (
            <ChangePassword
              open={isChangePasswordOpen}
              onClose={handleCloseChangePassword}
            />
          )}
          {tabValue === 2 && <EstimateSettings />}
          {tabValue === 3 && <GmailIntegration />}
        </div>
      </div>
    </div>
  );
};

export default Settings;